import type { VoiceEndReason } from '@myrewards/contracts';
import { HELP_VOICE_RECORDING_MS } from '@myrewards/domain';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { aiKeys, aiStatusQuery, helpVoiceTicket } from '../api/ai';
import { useReducedMotion } from '../design/motion';
import {
  explainMicrophone,
  isMicrophoneError,
  watchMicrophone,
  type MicrophoneHelp,
} from '../feedback/voice/micAccess';
import { openMicrophone } from '../feedback/voice/microphone';
import {
  clock,
  FINISHING,
  RECORDING,
  STARTING,
  STILL,
  useElapsed,
  useLevelBars,
} from '../feedback/voice/RecordingBar';
import {
  startRecorder,
  voiceUrl,
  wordsOf,
  type Recorder,
  type RecorderPhase,
  type RecorderResult,
} from '../feedback/voice/recorder';
import { Icon } from '../ui';
import s from './HelpDictation.module.css';

// APP-124: dictating a Help chat question, the way feedback dictates (APP-104 §3.2, §8): the same
// microphone, recorder, socket and speech, with a slim strip in place of the composer's row. The
// words fill the question box as they come; Done leaves them there for the person to check and
// send with Ask, and Cancel puts the box back as it was.

const VOICE_USED = 'You’ve used today’s voice time. Typing works as usual.';
const NOT_STARTED =
  'Voice couldn’t start, so nothing you said was transcribed. Type your question instead.';
const TOO_MANY = 'Another recording is still finishing. Try again in a moment.';
const UNCONFIRMED =
  'We couldn’t confirm every word was transcribed. Check your question before you ask.';
const SLOW = 'The connection is slow, so we stopped recording. Check your question before you ask.';
const DAY_USED = 'That used the rest of today’s voice time.';
const ENDED_BY_ITSELF: Partial<Record<VoiceEndReason, string>> = {
  // APP-174: Help stops a few seconds after the words end, or after 15 s if none come, so the
  // line names no number.
  silence: 'Recording stopped when you went quiet.',
  no_audio: 'Recording stopped because no sound came through for 15 seconds.',
  recording_cap: 'Recording stops at 2 minutes.',
  daily_limit: DAY_USED,
};
/** Lines that stay once a question is asked: today's voice time is gone, so the microphone is. */
const LASTING: ReadonlySet<string> = new Set([VOICE_USED, DAY_USED]);

export type DictationPhase = RecorderPhase | 'idle';

export interface HelpDictation {
  phase: DictationPhase;
  /** When the microphone was first heard (ms), for the timer. */
  since: number | null;
  level: { current: number };
  /** What's been heard so far this recording: finished phrases, then the one being spoken. */
  heard: string;
  /** The last finished phrase and how many so far, for the polite announcement. */
  lastPhrase: string;
  phraseCount: number;
  /** A calm line about the last recording, or null. */
  notice: string | null;
  /** What stopped the microphone and the fix for this browser, or null. */
  mic: MicrophoneHelp | null;
  /** The microphone button shows (voice is offered and nothing here has turned it off). */
  offered: boolean;
  start: () => void;
  done: () => void;
  cancel: () => void;
  /** The question went: a line about the last recording goes with it (APP-174). */
  asked: () => void;
}

/**
 * One recording at a time. `onWords` gets what a recording gave, once it ends (never after
 * Cancel). Leaving the panel, or the chat closing, cancels whatever is under way.
 */
export function useHelpDictation({
  offered,
  secondsLeft,
  onWords,
}: {
  /** Voice for Help chat is offered to this person now (`/v1/ai/status`). */
  offered: boolean;
  /** Voice time left today, in seconds; null when unknown. */
  secondsLeft: number | null;
  onWords: (words: string) => void;
}): HelpDictation {
  const queryClient = useQueryClient();
  const [phase, setPhase] = useState<DictationPhase>('idle');
  const [since, setSince] = useState<number | null>(null);
  const [phrases, setPhrases] = useState<readonly string[]>([]);
  const [partial, setPartial] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [mic, setMic] = useState<MicrophoneHelp | null>(null);
  /** Voice can't be offered again while the chat is open (the day's time, or the server said no). */
  const [voiceOff, setVoiceOff] = useState(false);
  const level = useRef(0);
  const recorder = useRef<Recorder | null>(null);
  /** The recording now; events from an earlier one (cancelled) are ignored. */
  const current = useRef<object | null>(null);
  /** No ticket came because today's voice time had run out. */
  const usedUp = useRef(false);
  const wordsTo = useRef(onWords);
  useEffect(() => {
    wordsTo.current = onWords;
  });

  useEffect(
    () => () => {
      current.current = null;
      recorder.current?.cancel();
      recorder.current = null;
    },
    [],
  );

  // A blocked microphone: once the browser allows it, the help goes and the button works again.
  useEffect(() => {
    if (mic === null) return;
    return watchMicrophone(mic, () => {
      setMic(null);
    });
  }, [mic]);

  const reset = (): void => {
    setPhase('idle');
    setSince(null);
    setPhrases([]);
    setPartial('');
  };

  const ended = useCallback(
    async (result: RecorderResult): Promise<void> => {
      recorder.current = null;
      current.current = null;
      // The day's voice time has changed.
      void queryClient.invalidateQueries({ queryKey: aiKeys.status });
      reset();
      const said = wordsOf(result);
      if (said !== '') wordsTo.current(said);
      const end = result.end;
      switch (end.kind) {
        case 'saved':
          setNotice(ENDED_BY_ITSELF[end.reason] ?? null);
          if (end.reason === 'daily_limit') setVoiceOff(true);
          return;
        case 'typed':
          return;
        case 'unconfirmed':
          setNotice(UNCONFIRMED);
          return;
        case 'slow':
          setNotice(SLOW);
          return;
        case 'not_started':
          if (end.code === 'daily_limit' || (end.code === 'unavailable' && usedUp.current)) {
            setNotice(VOICE_USED);
            setVoiceOff(true);
          } else if (end.code === 'too_many_sessions') setNotice(TOO_MANY);
          else {
            setNotice(NOT_STARTED);
            // Voice isn't offered now (a switch, the audience, Help chat itself).
            if (end.code === 'unavailable' || end.code === 'ticket_invalid') setVoiceOff(true);
          }
          return;
        case 'microphone':
          if (isMicrophoneError(end.error)) setMic(await explainMicrophone(end.error));
          else {
            // This browser can't give a microphone to the page at all.
            setNotice(NOT_STARTED);
            setVoiceOff(true);
          }
          return;
      }
    },
    [queryClient],
  );

  const start = useCallback((): void => {
    if (recorder.current !== null) return;
    setNotice(null);
    setMic(null);
    if (secondsLeft === 0) {
      setNotice(VOICE_USED);
      return;
    }
    const token = {};
    current.current = token;
    const mine = (): boolean => current.current === token;
    usedUp.current = false;
    level.current = 0;
    reset();
    setPhase('starting');
    recorder.current = startRecorder({
      openMicrophone,
      ticket: async () => {
        const got = await helpVoiceTicket();
        if (got.ticket !== null) return { scope: 'help', ticket: got.ticket };
        // No ticket: was it today's time running out (a calm line), or voice being off now?
        try {
          const status = await queryClient.query({ ...aiStatusQuery, staleTime: 0 });
          usedUp.current = status.helpVoice === true && status.voiceSecondsLeft === 0;
        } catch {
          usedUp.current = false;
        }
        return null;
      },
      connect: () => new WebSocket(voiceUrl()),
      capMs:
        secondsLeft === null
          ? HELP_VOICE_RECORDING_MS
          : Math.min(HELP_VOICE_RECORDING_MS, secondsLeft * 1_000),
      events: {
        phase: (p) => {
          if (!mine()) return;
          setPhase(p);
          if (p === 'recording') setSince(Date.now());
        },
        level: (rms) => {
          level.current = Math.max(level.current, Math.min(1, rms * 6));
        },
        partial: (text) => {
          if (mine()) setPartial(text);
        },
        phrase: (p) => {
          if (!mine()) return;
          setPhrases((list) => [...list, p.text.trim()]);
          setPartial('');
        },
        limit: () => undefined,
        end: (result) => {
          if (mine()) void ended(result);
        },
      },
    });
  }, [ended, queryClient, secondsLeft]);

  const done = useCallback((): void => {
    recorder.current?.done();
  }, []);

  const cancel = useCallback((): void => {
    current.current = null;
    recorder.current?.cancel();
    recorder.current = null;
    reset();
    void queryClient.invalidateQueries({ queryKey: aiKeys.status });
  }, [queryClient]);

  const asked = useCallback((): void => {
    setNotice((line) => (line !== null && LASTING.has(line) ? line : null));
  }, []);

  return {
    phase,
    since,
    level,
    heard: [...phrases, partial.trim()].filter((w) => w !== '').join(' '),
    lastPhrase: phrases.at(-1) ?? '',
    phraseCount: phrases.length,
    notice,
    mic,
    offered: offered && !voiceOff,
    start,
    done,
    cancel,
    asked,
  };
}

/** The microphone beside Ask, the way ChatGPT and Claude put one in their prompt box. */
export function DictateButton({
  isDisabled,
  onPress,
}: {
  isDisabled: boolean;
  onPress: () => void;
}): ReactElement {
  return (
    <AriaButton
      className={s.mic}
      aria-label="Dictate your question"
      isDisabled={isDisabled}
      onPress={onPress}
    >
      <Icon name="mic" size={20} />
    </AriaButton>
  );
}

/**
 * The composer's row while dictating: the state, the timer and level bars, Cancel (✕) and Done
 * (✓). It takes focus when it opens, so Done is two Tabs away.
 */
export function DictationStrip({
  dictation,
  phase,
}: {
  dictation: HelpDictation;
  /** The recording's phase (never idle while the strip shows). */
  phase: RecorderPhase;
}): ReactElement {
  const { since, level, lastPhrase, phraseCount } = dictation;
  const still = useReducedMotion();
  const region = useRef<HTMLDivElement>(null);
  const levels = useRef<HTMLSpanElement>(null);
  const elapsed = useElapsed(phase, since);
  useLevelBars(levels, level, phase, still);

  useEffect(() => {
    region.current?.focus({ preventScroll: true });
  }, []);

  return (
    <div ref={region} tabIndex={-1} className={s.strip} role="group" aria-label="Dictation">
      <div className={s.row}>
        <div className={s.state}>
          {phase === 'recording' ? (
            <span className={s.dot} data-still={still ? 'true' : 'false'} aria-hidden="true" />
          ) : (
            <span className={s.spinner} data-still={still ? 'true' : 'false'} aria-hidden="true" />
          )}
          <p className={s.status} role="status">
            {phase === 'starting' ? STARTING : phase === 'recording' ? RECORDING : FINISHING}
          </p>
          {phase === 'recording' && (
            <>
              <span className={s.timer}>
                <span className="visually-hidden">Time recorded: </span>
                <span>{clock(elapsed)}</span>
              </span>
              <span ref={levels} className={s.levels} aria-hidden="true">
                {STILL.map((transform, i) => (
                  <span key={i} className={s.levelBar} style={{ transform }} />
                ))}
              </span>
            </>
          )}
        </div>
        <div className={s.actions}>
          <AriaButton className={s.cancel} aria-label="Cancel dictation" onPress={dictation.cancel}>
            <Icon name="close" size={18} />
          </AriaButton>
          {/* Round ✕ and ✓, as ChatGPT and Claude end dictation: they keep the strip on one row
              in the narrow panel. */}
          {phase === 'recording' && (
            <AriaButton className={s.done} aria-label="Done" onPress={dictation.done}>
              <Icon name="check" size={20} />
            </AriaButton>
          )}
        </div>
      </div>
      <p className={s.note}>Transcribed by Azure AI Speech. Audio isn’t kept.</p>
      <p className="visually-hidden" aria-live="polite">
        {/* A new node for each phrase, so the same words said again are read again. */}
        <span key={phraseCount}>{lastPhrase}</span>
      </p>
    </div>
  );
}
